import React from 'react';
import PropTypes from 'prop-types';
import { setClassNames } from '@ddc/classnames-js';
import FragmentContent from './FragmentContent';
import useDeviceType from '../hooks/useDeviceType';
import { useScrollContainer } from '../context/ScrollContainerContext';
import { FRAGMENT_STATUS } from '../features/fragmentsSlice';

/**
 * Maps a CSS class detected in the fragment HTML to class-list mutations
 * applied to the fragment container.
 *
 *   add             — classes always appended to the container
 *   exclude         — base classes to remove on all device types
 *   excludeOnMobile — base classes to remove when deviceType is 'MOBILE'
 *
 * Adding a new fragment type only requires a new entry here — no component
 * logic changes needed (Open/Closed).
 *
 * @type {Record<string, FragmentClassConfig>}
 */
const FRAGMENT_CLASS_MAP = {
	'hours-default': {
		add: ['py-4', 'ddc-font-size-small'],
		excludeOnMobile: ['px-4']
	},
	// ws-model-selector's row card (cardLayout=row) sets its own horizontal
	// inset via the drawer-scoped --drawer-horiz-pad-inner rule in
	// _fragment.scss — px-4 would double that padding. 'ms-row-card' only
	// appears in the row layout's markup (Row.jsx/Responsive.jsx), never in
	// stacked/GridCard, so this can't affect other fragment types.
	'ms-row-card': {
		exclude: ['px-4']
	}
};

// 'ws-nav-drawer__fragment' is the shared marker MegaMenuFragment also applies —
// every fragment-scoped rule in _fragment.scss keys off it (hover/active, padding,
// the <li> suppression block). Keep both classes so those rules reach the drawer too.
const BASE_CLASSES = [
	'ws-nav-drawer__fragment',
	'ws-nav-drawer__fragment-content',
	'px-4'
];

/**
 * Resolves the final class list for a loaded fragment by applying
 * FRAGMENT_CLASS_MAP entries matched against the HTML content.
 *
 * @param {string|null} html - sanitized fragment HTML
 * @param {string|undefined} deviceType - 'MOBILE' | 'DESKTOP'
 * @returns {string[]}
 */
const resolveFragmentClasses = (html, deviceType) => {
	const isMobile = deviceType === 'MOBILE';
	const matched = Object.entries(FRAGMENT_CLASS_MAP).filter(([detectClass]) =>
		html?.includes(detectClass)
	);

	const add = matched.flatMap(([, config]) => config.add || []);
	const exclude = new Set([
		...matched.flatMap(([, config]) => config.exclude || []),
		...(isMobile
			? matched.flatMap(([, config]) => config.excludeOnMobile || [])
			: [])
	]);

	const base = BASE_CLASSES.filter((c) => !exclude.has(c));
	return [...base, ...add];
};

/**
 * DrawerNavFragment — thin wrapper over FragmentContent that supplies the
 * drawer's real scroll-container observer root and its deviceType-aware
 * class-list policy. Lazy-fetches a fragment URL and renders the HTML inline,
 * showing a skeleton while loading and collapsing silently on error.
 *
 * The observer root comes from the nearest ScrollContainerContext (each
 * keep-mounted drilldown body provides its own), not a single shared ref.
 *
 * @param {DrawerNavFragmentProps} props
 */
const DrawerNavFragment = ({ url, skeletonHeight = '12rem' }) => {
	const scrollContainerRef = useScrollContainer();
	const deviceType = useDeviceType();

	const resolveClassName = ({ status, html }) => {
		const isLoading =
			status === FRAGMENT_STATUS.IDLE ||
			status === FRAGMENT_STATUS.LOADING;
		const resolvedClasses =
			status === FRAGMENT_STATUS.LOADED
				? resolveFragmentClasses(html, deviceType)
				: BASE_CLASSES;
		return setClassNames([
			...resolvedClasses,
			isLoading && 'ws-nav-drawer__fragment-content--loading'
		]);
	};

	return (
		<FragmentContent
			url={url}
			root={scrollContainerRef}
			classNameResolver={resolveClassName}
			skeletonHeight={skeletonHeight}
			// Every drawer fragment (drilldown + submenu) flows through here, so
			// this is the single place that marks the drawer surface. An embedded
			// ws-rec-vehicles reads it (containerRef.closest) for its 2-column
			// layout. The mega menu uses MegaMenuFragment, which does not set it.
			recVehiclesContext="nav-drawer"
			// Drilldown bodies are keep-mounted (they toggle display, never
			// unmount), so re-initializing on every reshow would leak DOM nodes +
			// listeners per reopen. Opt out and init once, matching MegaMenuFragment.
			// The one-time mount re-init (for a url already cached by the mega menu)
			// still runs to scope this instance's widget ids — so a dual-embedded
			// widget's bundle runs at most twice/page (≤1 "invoked multiple times"
			// warning). That's bounded, not the leak returning — don't re-enable
			// reshow re-init to "fix" it.
			reinitOnReshow={false}
		/>
	);
};

DrawerNavFragment.propTypes = {
	url: PropTypes.string,
	skeletonHeight: PropTypes.string
};

export default DrawerNavFragment;
